/**
 * Winkelwagen Store (Zustand)
 * Global state management voor shopping cart
 */

import { create } from 'zustand';
import { countryShippingGross } from '@/lib/commerce/rates-fallback';
import { persist, createJSONStorage } from 'zustand/middleware';
import { cartItemDisplayPrice, getVATRate } from '@/lib/pricing';
import { persistedStudioDesignFields } from '@/lib/studio/boog-cart';
import { quoteBasketDisplayForCountry } from '@/lib/quotes/quote-payment-price';
import type { CartQuantityPolicy } from '@/lib/cart/quantity-steps';
import { resolvePrintfulLineQuantity } from '@/lib/printful/quantity';

const noopStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

export interface CartItem {
  id: string; // Unieke ID voor cart item (niet product ID)
  productId: string;
  productName: string;
  productSlug: string;
  providerType?: string;
  category: string;
  productThumbnail?: string; // Product afbeelding URL

  // Configuratie
  configuration: {
    options: Record<string, string | number>;
    quantity: number;
    displayOptions?: Array<{
      key: string;
      label: string;
      value: string;
    }>;
  };

  // Pricing (incl. BTW), bevroren op het tarief van het moment van
  // toevoegen of van de laatste herprijzing.
  unitPrice: number;
  totalPrice: number;
  // Prijs EXCLUSIEF btw: verandert niet bij een landwissel, dus hieruit
  // wordt de brutoprijs bij weergave opnieuw berekend met het tarief van het
  // actuele land (zie cartItemDisplayPrice in src/lib/pricing.ts). Optioneel
  // voor achterwaartse compatibiliteit: winkelwagens die in localStorage
  // stonden voor deze wijziging hebben dit veld niet en vallen terug op de
  // bevroren unitPrice/totalPrice hierboven.
  unitPriceExVat?: number;
  totalPriceExVat?: number;
  pricingQuote?: {
    hash: string;
    createdAt: string;
    expiresAt: string;
    countryCode?: string;
  };
  // Technische informatie uit de leveranciersquote. De definitieve
  // verzendprijs komt altijd uit de server-gevalideerde carrierquote.
  shippingIncluded?: boolean;
  supplierShippingExVat?: number;
  // Regel via een handmatige leveranciersroute (bijv. dekbedovertrek
  // levering=snel, option-supplier-route.ts). Zulke regels delen een zending,
  // dus de winkelwagen prijst ze als mandje opnieuw (shared-shipment-repricing.ts).
  optionSupplierRoute?: boolean;
  printComQuoteMeta?: {
    supplierId: string;
    supplierCountry?: string;
    pricedOptions: Record<string, string | number>;
    deliveryPromise: number;
  };

  // File upload
  uploadedFile?: {
    id: string;
    fileName: string;
    fileSize: number;
    url: string;
    designData?: any; // Saved canvas design from editor
    providerFileId?: string;
  };
  uploadedFiles?: Array<{
    id: string;
    fileName: string;
    fileSize: number;
    url: string;
    designData?: any;
    providerFileId?: string;
    role?: string;
    printPages?: number[];
  }>;

  /**
   * Hoort deze regel bij een geaccepteerde offerte? Dan gaat de token mee naar
   * de kassa en houdt de server het afgesproken bedrag aan in plaats van de
   * prijs van vandaag. Alleen de token: de bedragen komen uit de database, want
   * wat in de winkelwagen staat is aan te passen in de browser.
   */
  quoteToken?: string;
  /** Display only. The order API independently reads accepted amounts from DB. */
  quoteDisplay?: { countryCode: string; subtotal: number; shipping: number; tax: number; total: number; discount: number; lineCount?: number; vatRateApplied?: number; reverseCharge?: boolean };
  /** Customer-entered quote details, used to seed checkout; VAT is revalidated server-side. */
  quoteCheckout?: { isBusiness: boolean; vatNumber: string; company: string; email: string };

  /** Code van de EditorSession die dit ontwerp maakte, indien van toepassing. */
  editorSessionCode?: string;

  /**
   * Toegestane aantallen van het product (staffel of open bereik), vastgelegd
   * bij het toevoegen zodat de stepper in de winkelwagen nooit een aantal
   * aanvraagt dat de prijsmotor afwijst. Ontbreekt bij oudere regels.
   */
  quantityPolicy?: CartQuantityPolicy;

  // Timestamps
  addedAt: Date;
}

interface CartStore {
  items: CartItem[];
  selectedCountryCode: string | null;
  /**
   * True zodra de klant zelf een land koos (landkeuze in de winkelwagen,
   * afleveradres in de kassa, offerte). Zolang dat niet zo is volgt de
   * winkelwagen het land van de taal, net als de productpagina: anders zag een
   * Duitse bezoeker zijn prijs bij het openen van de winkelwagen stijgen van
   * 19% naar 21% btw (19 okt: 15,63 -> 15,89 en 156,91 -> 159,55).
   */
  countryChosenByCustomer?: boolean;

  // Country data (cached from API)
  countryData: {
    vatRate: number;
    defaultShippingCost: number;
    freeShippingFrom: number;
    currency: string;
  } | null;

  // Actions
  addItem: (item: Omit<CartItem, 'id' | 'addedAt'>) => void;
  addItems: (items: Array<Omit<CartItem, 'id' | 'addedAt'>>) => void;
  removeItem: (itemId: string) => void;
  updateConfiguredItem: (itemId: string, updates: Pick<CartItem, 'configuration' | 'unitPrice' | 'totalPrice'> &
    Partial<Pick<CartItem, 'unitPriceExVat' | 'totalPriceExVat' | 'pricingQuote' | 'shippingIncluded' | 'supplierShippingExVat' | 'optionSupplierRoute' | 'printComQuoteMeta' | 'uploadedFile' | 'uploadedFiles' | 'editorSessionCode'>>) => boolean;
  updateQuantity: (itemId: string, quantity: number) => void;
  updateItemPricing: (
    itemId: string,
    updates: Pick<CartItem, 'configuration' | 'unitPrice' | 'totalPrice'> &
      Partial<Pick<CartItem, 'pricingQuote' | 'unitPriceExVat' | 'totalPriceExVat'>>
      & Partial<Pick<CartItem, 'shippingIncluded' | 'supplierShippingExVat' | 'optionSupplierRoute'>>
      & Partial<Pick<CartItem, 'printComQuoteMeta'>>
  ) => void;
  /**
   * Zet een regel uit een oud productmodel om (cart preflight `options_migrated`
   * of `product_replaced`): nieuw product, leverancierstype en opties. De oude
   * prijsbevestiging vervalt, zodat de winkelwagen de regel opnieuw prijst.
   */
  migrateItem: (
    itemId: string,
    updates: Pick<CartItem, 'configuration'> & Partial<Pick<CartItem, 'productId' | 'productSlug' | 'productName' | 'providerType'>>
  ) => void;
  clearCart: () => void;
  setCountry: (countryCode: string) => void;
  /** Zet het land van de taal, tenzij de klant al zelf een land koos. */
  applyLocaleCountry: (countryCode: string) => void;
  loadCountryData: () => Promise<void>;

  // Computed
  getTotalItems: () => number;
  getSubtotal: () => number;
  getVAT: () => number;
  getShipping: () => number;
  getTotal: () => number; // Including tax & shipping
  // Het btw-tarief van het actuele land: uit countryData zodra geladen,
  // anders een per-land noodvangnet (nooit hardcoded NL), zie
  // getVATRate() in src/lib/pricing.ts.
  getEffectiveVatRate: () => number;
  // Weergaveprijs van een regel met het tarief van het actuele land, in
  // plaats van het bevroren tarief van het moment van toevoegen (zie
  // cartItemDisplayPrice in src/lib/pricing.ts).
  getItemDisplayUnitPrice: (item: CartItem) => number;
  getItemDisplayTotalPrice: (item: CartItem) => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      selectedCountryCode: null,
      countryData: null,

      addItem: (item) => {
        const newItem: CartItem = {
          ...item,
          id: `cart-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          addedAt: new Date(),
        };

        set((state) => ({
          items: [...state.items, newItem],
        }));
      },

      // One persisted update: a board and its configured prints cannot be
      // interrupted by navigation between separate addItem calls.
      addItems: (items) => {
        const addedAt = new Date();
        const newItems = items.map(item => ({
          ...item,
          id: `cart-${crypto.randomUUID()}`,
          addedAt,
        }));
        set(state => ({ items: [...state.items, ...newItems] }));
      },

      removeItem: (itemId) => {
        set((state) => ({
          items: state.items.filter((item) => item.id !== itemId),
        }));
      },

      updateConfiguredItem: (itemId, updates) => {
        const current = get().items.find((item) => item.id === itemId);
        if (!current || current.quoteToken) return false;
        set((state) => ({
          items: state.items.map((item) => item.id === itemId
            ? { ...item, ...updates, editorSessionCode: updates.editorSessionCode ?? item.editorSessionCode }
            : item),
        }));
        return true;
      },

      updateQuantity: (itemId, quantity) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  configuration: { ...item.configuration, quantity },
                  totalPrice: item.unitPrice * quantity,
                  // unitPriceExVat blijft gelijk (prijs per stuk verandert
                  // niet), dus totalPriceExVat schaalt daar recht evenredig
                  // mee mee. Onbekend bij oude winkelwagens: dan blijft het
                  // veld ontbreken, net als hierboven al het geval was.
                  totalPriceExVat: typeof item.unitPriceExVat === 'number' && Number.isFinite(item.unitPriceExVat)
                    ? Number((item.unitPriceExVat * quantity).toFixed(2))
                    : item.totalPriceExVat,
                }
              : item
          ),
        }));
      },

      updateItemPricing: (itemId, updates) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  configuration: updates.configuration,
                  unitPrice: updates.unitPrice,
                  totalPrice: updates.totalPrice,
                  pricingQuote: updates.pricingQuote,
                  shippingIncluded: updates.shippingIncluded ?? item.shippingIncluded,
                  supplierShippingExVat: updates.supplierShippingExVat ?? item.supplierShippingExVat,
                  optionSupplierRoute: updates.optionSupplierRoute ?? item.optionSupplierRoute,
                  printComQuoteMeta: Object.prototype.hasOwnProperty.call(updates, 'printComQuoteMeta')
                    ? updates.printComQuoteMeta : item.printComQuoteMeta,
                  // ?? behoudt een eerder bekende ex-btw-prijs als deze
                  // aanroep die niet meestuurt, in plaats van hem stil te
                  // wissen.
                  unitPriceExVat: updates.unitPriceExVat ?? item.unitPriceExVat,
                  totalPriceExVat: updates.totalPriceExVat ?? item.totalPriceExVat,
                }
              : item
          ),
        }));
      },

      migrateItem: (itemId, updates) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  productId: updates.productId || item.productId,
                  productSlug: updates.productSlug || item.productSlug,
                  productName: updates.productName || item.productName,
                  providerType: updates.providerType || item.providerType,
                  configuration: updates.configuration,
                  pricingQuote: undefined,
                  printComQuoteMeta: undefined,
                }
              : item
          ),
        }));
      },

      clearCart: () => {
        set({ items: [] });
      },

      setCountry: (countryCode) => {
        set((state) => ({
          selectedCountryCode: countryCode,
          countryChosenByCustomer: true,
          countryData: state.selectedCountryCode === countryCode ? state.countryData : null,
        }));
        get().loadCountryData();
      },

      applyLocaleCountry: (countryCode) => {
        const state = get();
        if (state.countryChosenByCustomer) return;
        if (state.selectedCountryCode === countryCode && state.countryData) return;
        set({
          selectedCountryCode: countryCode,
          countryData: state.selectedCountryCode === countryCode ? state.countryData : null,
        });
        get().loadCountryData();
      },

      loadCountryData: async () => {
        const countryCode = get().selectedCountryCode;
        if (!countryCode) return;

        try {
          const response = await fetch(`/api/countries/${countryCode}`);
          if (response.ok) {
            const data = await response.json();
            // A slower previous country request must not overwrite the current
            // destination's VAT and shipping settings.
            if (get().selectedCountryCode !== countryCode) return;
            set({
              countryData: {
                vatRate: data.vatRate,
                defaultShippingCost: data.defaultShippingCost,
                freeShippingFrom: data.freeShippingFrom,
                currency: data.currency,
              },
            });
          }
        } catch (error) {
          console.error('Failed to load country data:', error);
        }
      },

      getTotalItems: () => {
        return get().items.reduce((total, item) => {
          if (item.providerType === 'PRINTFUL') {
            return total + resolvePrintfulLineQuantity(item.configuration.options, item.configuration.quantity);
          }
          const copies = Number(item.configuration.options?.copies);
          const quantity = Number.isFinite(copies) && copies > 0 ? copies : item.configuration.quantity;
          return total + quantity;
        }, 0);
      },

      getSubtotal: () => {
        const quote = quoteBasketDisplayForCountry(get().items, get().selectedCountryCode || 'NL');
        if (quote) return quote.subtotal;
        // Telt de weergaveprijs per regel op (met het tarief van het actuele
        // land), niet de bevroren totalPrice: anders klopt de gratis-
        // verzendendrempel niet meer zodra het land na het toevoegen wisselt.
        const vatRate = get().getEffectiveVatRate();
        return get().items.reduce(
          (total, item) => total + cartItemDisplayPrice(item.totalPriceExVat, vatRate, item.totalPrice),
          0
        );
      },

      getVAT: () => {
        const quote = quoteBasketDisplayForCountry(get().items, get().selectedCountryCode || 'NL');
        if (quote) return quote.tax;
        // Btw wordt uit het bruto totaal gehaald, zoals de kassa dat doet (zie
        // extractVatFromGross in src/lib/commerce/rates.ts): over subtotaal
        // plus verzendkosten, niet over het subtotaal alleen. Anders toont de
        // winkelwagen een ander btw-bedrag dan de checkoutpagina zodra er
        // verzendkosten in rekening worden gebracht.
        const total = get().getTotal();
        const vatRate = get().getEffectiveVatRate();
        return total > 0 ? total - total / (1 + vatRate / 100) : 0;
      },

      getShipping: () => {
        const quote = quoteBasketDisplayForCountry(get().items, get().selectedCountryCode || 'NL');
        if (quote) return quote.shipping;
        const countryData = get().countryData;

        // Noodvangnet zolang /api/countries/{code} nog niet is geladen: de
        // fallback van het gekozen land, nooit het NL-tarief voor een ander land.
        return countryShippingGross(get().selectedCountryCode, countryData?.defaultShippingCost);
      },

      getTotal: () => {
        const quote = quoteBasketDisplayForCountry(get().items, get().selectedCountryCode || 'NL');
        if (quote) return quote.total;
        const subtotal = get().getSubtotal();
        const shipping = get().getShipping();
        return subtotal + shipping;
      },

      getEffectiveVatRate: () => {
        const countryData = get().countryData;
        if (countryData) return countryData.vatRate;
        // countryData is nog niet geladen (zie loadCountryData): val terug op
        // het per-land tarief in plaats van altijd NL, zodat een net
        // gewisseld land niet eventjes met het verkeerde tarief wordt getoond.
        return getVATRate(get().selectedCountryCode || 'NL');
      },

      getItemDisplayUnitPrice: (item) => {
        return cartItemDisplayPrice(item.unitPriceExVat, get().getEffectiveVatRate(), item.unitPrice);
      },

      getItemDisplayTotalPrice: (item) => {
        return cartItemDisplayPrice(item.totalPriceExVat, get().getEffectiveVatRate(), item.totalPrice);
      },
    }),
    {
      name: 'cart-storage',
      storage: createJSONStorage(() => (typeof window === 'undefined' ? noopStorage : localStorage)),
      // Opgeslagen landtarieven zijn nooit de waarheid: ze tonen alleen meteen
      // iets (stale-while-revalidate). Bij elk paginabezoek haalt de winkelwagen
      // het actuele tarief op (/api/countries, dezelfde bron als de kassa).
      onRehydrateStorage: () => (state) => {
        if (state?.selectedCountryCode) void state.loadCountryData();
      },
      partialize: (state) => ({
        ...state,
        items: state.items.map((item) => ({
          ...item,
          uploadedFile: item.uploadedFile ? {
            id: item.uploadedFile.id,
            fileName: item.uploadedFile.fileName,
            fileSize: item.uploadedFile.fileSize,
            url: item.uploadedFile.url,
            // Studio designs contain only small editable controls and image URLs.
            ...persistedStudioDesignFields(item.uploadedFile.designData),
            providerFileId: item.uploadedFile.providerFileId,
          } : undefined,
          uploadedFiles: item.uploadedFiles?.map((f) => ({
            id: f.id,
            fileName: f.fileName,
            fileSize: f.fileSize,
            url: f.url,
            ...persistedStudioDesignFields(f.designData),
            providerFileId: f.providerFileId,
            role: f.role,
            printPages: f.printPages,
          })),
          // Exclude pricing quote — regenerated on next page load
          pricingQuote: undefined,
          printComQuoteMeta: undefined,
        })),
      }),
    }
  )
);
